import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RUPEE } from "../../constants/globalConstants";
import { appApiHandler } from "../../api/appApiHandler";
import {
  getErrorMessage,
  getImageUrl,
  getOptimizedUrl,
  getPrice,
  getInitials,
} from "../../constants/globalFunction";
import { setWhishlist } from "../../store/slices/whishlistState";
import { setCartCount, setWhishlistCount } from "../../store/slices/countState";
import {
  FaHeart,
  FaRegHeart,
  FaDownload,
  FaFileArchive,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaFileAlt,
  FaSyncAlt,
} from "react-icons/fa";
import JSZip from "jszip";
import ImageViewer from "../../components/ImageViewer";
import CommonButton from "../../components/button/CommonButton";
import CartDrawer from "../../components/cart/CartDrawer";
import "./ProductDetails.css";
import { colors } from "../../constants/colors";
import { showErrorToast } from "../../utils/toast";
import { string } from "../../constants/string";
import CommonFooter from "../../components/footer/CommonFooter";
import HomeHeader from "../../components/header/HomeHeader";
import Breadcrumb from "../../components/breadcrumb";
import LazyImage from "../../components/lazyImage/LazyImage";
import ProductCard from "../home/components/ProductCard";
import { getUserType } from "../../constants/asyncStoreManager";
import {
  buildRecentProductSnapshot,
  getRecentProductsUserKey,
  getRecentlyViewedProducts,
  recordRecentlyViewedProduct,
} from "../../utils/recentlyViewedProducts";
import { openWhatsAppProductInquiry } from "../../constants/whatsappContact";
import { imagePath } from "../../constants/imagePath";

const ProductDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userData } = useSelector((state) => state.userState);
  const { whishlist } = useSelector((state) => state.whishlistState);

  // Get product_id from URL params or navigation state
  const urlParams = new URLSearchParams(window.location.search);
  const urlProductId = urlParams.get("product_id");
  const {
    product_id: stateProductId,
    cartSelectedSize,
    cartSelectedColor,
    fromPage,
  } = location?.state || {};

  const product_id = urlProductId || stateProductId;

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [productPrice, setProductPrice] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [cartProducts, setCartProducts] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [sortedImages, setSortedImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [returnPolicyExpanded, setReturnPolicyExpanded] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);
  const [showStickyBar, setShowStickyBar] = useState(false);

  const scrollViewRef = useRef(null);
  const isManualScrollRef = useRef(false);
  const imageRef = useRef(null);
  const reviewsSectionRef = useRef(null);
  const footerRef = useRef(null);

  const isWishlisted = whishlist?.some(
    (wishItem) => wishItem?.product_id === product?._id
  );

  // Function to determine breadcrumb items based on source page
  const getBreadcrumbItems = () => {
    const baseItems = [
      {
        label: "Home",
        path: "/",
      },
    ];

    // If we have explicit fromPage in state, use it
    if (fromPage) {
      switch (fromPage) {
        case "home":
          return [
            ...baseItems,
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "wishlist":
          return [
            ...baseItems,
            {
              label: "My Wishlist",
              path: "/whishlist",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "cart":
          return [
            ...baseItems,
            {
              label: "Cart",
              path: "/cart",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "category":
          return [
            ...baseItems,
            {
              label: "Category",
              path: "/category",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "search":
          return [
            ...baseItems,
            {
              label: "Search",
              path: "/search",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        case "notification":
          return [
            ...baseItems,
            {
              label: "Notifications",
              path: "/notification",
            },
            {
              label: "Product Details",
              isActive: true,
            },
          ];
        default:
          return [
            ...baseItems,
            {
              label: "Product Details",
              isActive: true,
            },
          ];
      }
    }

    // Try to detect source from document.referrer (for new tab navigation)
    const referrer = document.referrer;
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const pathname = referrerUrl.pathname;
        const hostname = referrerUrl.hostname;

        // Only process if it's from the same domain
        if (hostname === window.location.hostname || hostname === "localhost") {
          if (pathname === "/" || pathname === "") {
            return [
              ...baseItems,
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/whishlist")) {
            return [
              ...baseItems,
              {
                label: "My Wishlist",
                path: "/whishlist",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/cart")) {
            return [
              ...baseItems,
              {
                label: "Cart",
                path: "/cart",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/category")) {
            return [
              ...baseItems,
              {
                label: "Category",
                path: "/category",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/search")) {
            return [
              ...baseItems,
              {
                label: "Search",
                path: "/search",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          } else if (pathname.includes("/notification")) {
            return [
              ...baseItems,
              {
                label: "Notifications",
                path: "/notification",
              },
              {
                label: "Product Details",
                isActive: true,
              },
            ];
          }
        }
      } catch (error) {
        console.log("Error parsing referrer URL:", error);
      }
    }

    // Default breadcrumb
    return [
      ...baseItems,
      {
        label: "Product Details",
        isActive: true,
      },
    ];
  };

  const getWishlistProducts = async () => {
    if (!userData?._id) return;
    const reqData = {
      user_id: userData._id,
    };
    const response = await appApiHandler.getWishlist(reqData);
    if (response?.status === 200 && response?.data) {
      dispatch(setWhishlist(response?.data));
      dispatch(setWhishlistCount(response?.data?.length));
    }
  };

  const getCartProducts = React.useCallback(async () => {
    if (!userData) {
      setCartProducts([]);
      return;
    }
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getCart(reqData);
    setCartLoading(false);
    if (response?.status === 200) {
      const products = response?.data?.products || [];
      dispatch(setCartCount(products.length));
      setCartProducts(products);
    }
  }, [userData, dispatch]);

  useEffect(() => {
    window.addEventListener('cartUpdated', getCartProducts);
    return () => window.removeEventListener('cartUpdated', getCartProducts);
  }, [getCartProducts]);

  const onPressWishlist = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }
    const reqData = {
      user_id: userData?._id,
      product_id: product?._id,
    };
    const response = isWishlisted
      ? await appApiHandler.removeFromWishlist(reqData)
      : await appApiHandler.addToWishlist(reqData);
    if (response?.status === 200) {
      getWishlistProducts();
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const getProductDetails = async () => {
    const reqData = {
      product_id: product_id,
    };
    const response = await appApiHandler.getProductDetails(reqData);
    if (response?.status === 200) {
      const productData = response?.data;
      setProduct(productData);
      if (productData?.colors && productData?.images) {
        const images = [];

        productData?.colors.forEach((color) => {
          const colorImages = productData?.images.filter(
            (img) => img?.color?._id === color?._id
          );
          images.push(...colorImages);
        });

        const remainingImages = productData?.images.filter(
          (img) =>
            !img?.color ||
            !productData?.colors.some((color) => color?._id === img?.color?._id)
        );
        images.push(...remainingImages);

        setSortedImages(images);
      } else {
        setSortedImages(productData?.images || []);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!product_id) {
      setLoading(false);
      setProduct(null);

      return;
    }
    setLoading(true);
    getProductDetails();
    window.scrollTo(0, 0);
  }, [product_id]);

  useEffect(() => {
    if (!userData?._id) return;
    getCartProducts();
  }, [userData?._id]);

  // Set breadcrumb items when component mounts
  useEffect(() => {
    setBreadcrumbItems(getBreadcrumbItems());
  }, [fromPage]);

  // Set initial active tab based on available content
  useEffect(() => {
    if (product) {
      // product description by default 
      setDescriptionExpanded(false);
      setReturnPolicyExpanded(false);

    }
  }, [product]);

  // Scroll listener for sticky add-to-cart bar
  useEffect(() => {
    const handleScroll = () => {
      if (!reviewsSectionRef.current || !footerRef.current) return;

      const reviewTrigger = reviewsSectionRef.current.getBoundingClientRect().top;
      const footerTrigger = footerRef.current.getBoundingClientRect().top;

      // Show sticky bar when we scroll past the review trigger point
      // and hide it when the footer is reached
      if (reviewTrigger < 80 && footerTrigger > 160) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [product]);

  const relatedCategoryId = useMemo(() => {
    return product?.category_id ?? null;
  }, [product?.category_id]);

  useEffect(() => {
    const currentId = product?._id;

    async function fetchRelated() {
      if (!relatedCategoryId) {
        setRelatedProducts([]);
        return;
      }

      try {
        const userType = (await getUserType()) || "retailer";
        const response = await appApiHandler.getProducts({
          user_type: userType,
          category_id: relatedCategoryId
        });

        if (response?.status !== 200) {
          setRelatedProducts([]);
          return;
        }

        const list = response?.data || [];
        const sid = String(currentId);
        const others = list.filter((p) => p && String(p._id) !== sid).slice(0, 16);
        setRelatedProducts(others);
      } catch {
        setRelatedProducts([]);
      }
    }

    fetchRelated();
  }, [product?._id, relatedCategoryId]);

  useEffect(() => {
    if (!product?._id) return;
    const userKey = getRecentProductsUserKey(userData);
    const snapshot = buildRecentProductSnapshot(product, sortedImages);
    recordRecentlyViewedProduct(userKey, snapshot);
    setRecentlyViewedProducts(
      getRecentlyViewedProducts(userKey, product._id)
    );
  }, [product, sortedImages, userData?._id]);

  const displayColors = useMemo(() => {
    if (!product?.colors || !sortedImages) return product?.colors || [];

    const filteredColors = product.colors.filter((color) => {
      if (
        color?.name === "No Color" &&
        color?.hex_code === "rgba(0, 0, 0, 0)"
      ) {
        return sortedImages.some((img) => img?.color?._id === color?._id);
      }
      return true;
    });

    const colorIdsWithImages = [];
    sortedImages.forEach((img) => {
      if (img?.color?._id && !colorIdsWithImages.includes(img?.color?._id)) {
        colorIdsWithImages.push(img?.color?._id);
      }
    });

    const colorsWithImages = colorIdsWithImages
      .map((id) => filteredColors.find((color) => color?._id === id))
      .filter(Boolean);

    const colorsWithoutImages = filteredColors.filter(
      (color) => !colorIdsWithImages.includes(color?._id)
    );

    const allColorOption = {
      _id: "all-color",
      name: "All",
      hex_code: "#000000",
    };

    // Find "No Color" option
    const noColorOption = [...colorsWithImages, ...colorsWithoutImages].find(
      (color) =>
        color?.name === "No Color" && color?.hex_code === "rgba(0, 0, 0, 0)"
    );

    // Get other colors (excluding "No Color")
    const otherColors = [...colorsWithImages, ...colorsWithoutImages].filter(
      (color) =>
        !(color?.name === "No Color" && color?.hex_code === "rgba(0, 0, 0, 0)")
    );

    // Return in the desired order: All Color, No Color, Other Colors
    const orderedColors = [allColorOption];
    if (noColorOption) {
      orderedColors.push(noColorOption);
    }
    orderedColors.push(...otherColors);

    return orderedColors.reverse();
  }, [product, sortedImages]);


  useEffect(() => {
    const getProductPrice = async () => {
      if (selectedSize) {
        const price = await getPrice(selectedSize);
        setProductPrice(price);
      }
    };

    if (product) {
      if (!selectedSize) {
        let sizeToSelect = null;

        if (cartSelectedSize) {
          const cartSize = product?.sizes?.find(
            (size) => size?._id === cartSelectedSize
          );

          if (cartSize && isProductInStock(cartSize?.unit)) {
            sizeToSelect = cartSize;
          }
        }

        if (!sizeToSelect) {
          sizeToSelect = product?.sizes?.find((size) =>
            isProductInStock(size?.unit)
          );
        }

        setSelectedSize(sizeToSelect);
      }

      if (!selectedColor) {
        let colorToSelect = null;

        if (cartSelectedColor) {
          colorToSelect = product?.colors?.find(
            (color) => color?._id === cartSelectedColor
          );
        }

        if (colorToSelect) {
          handleColorSelection(colorToSelect);
        } else {
          // const allColorOption = {
          //   _id: "all-color",
          //   name: "All",
          //   hex_code: "#000000",
          // };
          // setSelectedColor(allColorOption);
          const lastColor = displayColors?.[0];
          if (lastColor) {
            handleColorSelection(lastColor);
          }
        }
      }

      getProductPrice();
    }
  }, [product, selectedSize, displayColors]);

  const isProductInStock = (unit) => {
    return unit && unit > 0;
  };

  const handleColorSelection = (color) => {
    if (!sortedImages || !color) {
      return;
    }

    if (color?._id === "all-color") {
      setSelectedColor(color);
      return;
    }

    const imageIndex = sortedImages.findIndex(
      (img) => img?.color?._id === color?._id
    );
    if (imageIndex !== -1 && scrollViewRef.current) {
      isManualScrollRef.current = true;
      scrollViewRef.current.scrollTo({
        left: imageIndex * scrollViewRef.current.offsetWidth,
        behavior: "smooth",
      });
      setActiveIndex(imageIndex);
      setTimeout(() => {
        isManualScrollRef.current = false;
      }, 500);
    }

    setSelectedColor(color);
  };

  const openCartDrawer = () => {
    setIsCartDrawerOpen(true);
  };

  const closeCartDrawer = () => {
    setIsCartDrawerOpen(false);
    getCartProducts();
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const magnifierSize = 120;
    const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100));

    setZoomPosition({ x: xPercent, y: yPercent });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  // Preload zoom view for instant response
  const preloadZoomView = useMemo(() => {
    if (!filteredImages[activeIndex]?.image) return null;
    return getImageUrl(filteredImages[activeIndex]?.image);
  }, [filteredImages, activeIndex]);

  const handleAddToCart = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }
    if (!selectedColor || selectedColor?._id === "all-color") {
      showErrorToast("Please select a specific color");
      return;
    }
    const reqData = {
      user_id: userData?._id,
      product_id: product?._id,
      size_id: selectedSize?._id,
      color_id: selectedColor?._id,
      qty: 1,
    };
    console.log('add to cart end')
    setCartLoading(true);
    const response = await appApiHandler.addToCart(reqData);

    if (response?.status === 200) {
      getCartProducts();
    } else if (response?.message) {
      setCartLoading(false);
      showErrorToast(getErrorMessage(response?.message));
    } else {
      setCartLoading(false);
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const goToProductInquiry = () => {
    const name = product?.name || "Product";
    const pid = product?._id ?? product_id;
    const url = `${window.location.origin}/product?product_id=${encodeURIComponent(String(pid ?? ""))}`;
    openWhatsAppProductInquiry({
      productName: name,
      productPageUrl: url,
      productId: pid ?? "",
    });
  };

  const hasInStockSize = useMemo(
    () => product?.sizes?.some((size) => isProductInStock(size?.unit)),
    [product?.sizes]
  );

  const isProductInCart = useMemo(() => {
    if (!product || !selectedSize) return false;
    return cartProducts.some((item) => {
      const sameProduct = item?._id === product?._id;
      const sameSize = item?.size_id === selectedSize?._id;
      if (product?.colors?.length > 0) {
        return sameProduct && sameSize && item?.color_id === selectedColor?._id;
      } else {
        return sameProduct && sameSize;
      }
    });
  }, [cartProducts, product, selectedSize, selectedColor]);

  const handleDownloadImagesSingle = async () => {
    try {
      if (!sortedImages?.length) return;

      // Get product name for organized filenames
      const rawName = product?.name || "product";
      const productName = (
        rawName.length > 20 ? rawName.substring(0, 20) : rawName
      ).replace(/[^a-zA-Z0-9]/g, "_");

      // Organize images by color for better naming
      const imagesByColor = {};
      const imagesWithoutColor = [];

      sortedImages.forEach((img) => {
        if (img?.color) {
          const colorName = img?.color?.name || "Color";
          if (!imagesByColor[colorName]) {
            imagesByColor[colorName] = [];
          }
          imagesByColor[colorName].push(img);
        } else {
          imagesWithoutColor.push(img);
        }
      });

      let downloadCount = 0;

      // Download images organized by color
      for (const [colorName, images] of Object.entries(imagesByColor)) {
        const colorFolderName = colorName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, "_");

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          if (!img?.image) continue;

          const imageUrl = getImageUrl(img?.image);

          try {
            const response = await fetch(imageUrl, { mode: "cors" });
            if (!response.ok) throw new Error("Network response was not ok");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Create organized filename
            const originalFileName = img?.image?.split("/").pop() || "";
            const extension = originalFileName.includes(".")
              ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
              : "jpg";

            const fileName = `${productName}_${colorFolderName}_${i + 1
              }.${extension}`;

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
            downloadCount++;
          } catch (err) {
            console.error(
              `❌ Failed to download ${colorName} image ${i + 1}:`,
              err
            );
          }

          // Delay between downloads
          if (downloadCount < sortedImages.length) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Download images without color
      for (let i = 0; i < imagesWithoutColor.length; i++) {
        const img = imagesWithoutColor[i];
        if (!img?.image) continue;

        const imageUrl = getImageUrl(img?.image);

        try {
          const response = await fetch(imageUrl, { mode: "cors" });
          if (!response.ok) throw new Error("Network response was not ok");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          // Create organized filename
          const originalFileName = img?.image?.split("/").pop() || "";
          const extension = originalFileName.includes(".")
            ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
            : "jpg";

          const fileName = `${productName}_General_${i + 1}.${extension}`;

          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          window.URL.revokeObjectURL(url);
          downloadCount++;
        } catch (err) {
          console.error(`❌ Failed to download general image ${i + 1}:`, err);
        }

        // Delay between downloads
        if (downloadCount < sortedImages.length) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      console.error("Error in download function:", error);
    }
  };

  const handleDownloadImagesAsZip = async () => {
    try {
      if (!sortedImages?.length) return;
      setDownloading(true);
      setDownloadProgress(0);

      const rawName = product?.name || "product";
      const productName = (
        rawName.length > 20 ? rawName.substring(0, 20) : rawName
      ).replace(/[^a-zA-Z0-9]/g, "_");

      const zip = new JSZip();
      const productFolder = zip.folder(productName);

      const imagesByColor = {};
      const imagesWithoutColor = [];

      sortedImages.forEach((img) => {
        if (img?.color) {
          const colorName = img?.color?.name || "Color";
          if (!imagesByColor[colorName]) {
            imagesByColor[colorName] = [];
          }
          imagesByColor[colorName].push(img);
        } else {
          imagesWithoutColor.push(img);
        }
      });

      let totalImages = 0;
      Object.values(imagesByColor).forEach(
        (arr) => (totalImages += arr.length)
      );
      totalImages += imagesWithoutColor.length;
      let downloadedCount = 0;

      for (const [colorName, images] of Object.entries(imagesByColor)) {
        const colorFolderName = colorName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, "_");
        const colorFolder = productFolder.folder(colorFolderName);

        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          if (!img?.image) {
            continue;
          }

          const imageUrl = getImageUrl(img?.image);

          try {
            let response;
            try {
              response = await fetch(imageUrl, {
                mode: "cors",
                headers: {
                  Accept: "image/*",
                },
              });
            } catch (fetchError) {
              response = await fetch(imageUrl);
            }

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            const originalFileName = img?.image?.split("/").pop() || "";
            const extension = originalFileName.includes(".")
              ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
              : "jpg";

            const fileName = `${colorFolderName}_${i + 1
              }_${Date.now()}.${extension}`;

            colorFolder.file(fileName, blob);

            downloadedCount++;
            setDownloadProgress(
              Math.round((downloadedCount / totalImages) * 100)
            );
          } catch (err) {
            console.error(
              `❌ Failed to process ${colorName} image ${i + 1}:`,
              err
            );

            // Fallback: try to create a placeholder file
            const fileName = `${colorFolderName}_${i + 1}_error.txt`;
            colorFolder.file(
              fileName,
              `Failed to download: ${imageUrl}\nError: ${err.message}`
            );
          }
        }
      }

      // Process images without color
      for (let i = 0; i < imagesWithoutColor.length; i++) {
        const img = imagesWithoutColor[i];
        if (!img?.image) {
          continue;
        }

        const imageUrl = getImageUrl(img?.image);

        try {
          // Try different fetch approaches
          let response;
          try {
            response = await fetch(imageUrl, {
              mode: "cors",
              headers: {
                Accept: "image/*",
              },
            });
          } catch (fetchError) {
            console.log("CORS fetch failed, trying without CORS mode");
            response = await fetch(imageUrl);
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();

          // Create organized filename
          const originalFileName = img?.image?.split("/").pop() || "";
          const extension = originalFileName.includes(".")
            ? originalFileName.split(".").pop()?.substring(0, 20) || "jpg"
            : "jpg";

          const fileName = `${productName}_${i + 1}_${Date.now()}.${extension}`;

          productFolder.file(fileName, blob);

          downloadedCount++;
          setDownloadProgress(
            Math.round((downloadedCount / totalImages) * 100)
          );
        } catch (err) {
          console.error(`❌ Failed to process general image ${i + 1}:`, err);

          // Fallback: try to create a placeholder file
          const fileName = `${productName}_${i + 1}_error.txt`;
          productFolder.file(
            fileName,
            `Failed to download: ${imageUrl}\nError: ${err.message}`
          );
        }
      }

      // Generate and download ZIP file

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = zipUrl;
      link.download = `${productName}_${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(zipUrl);

      setDownloading(false);
      setDownloadProgress(0);
    } catch (error) {
      console.error("Error downloading images as zip:", error);
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  useEffect(() => {
    if (!selectedColor) {
      setFilteredImages(sortedImages);
      return;
    }

    if (selectedColor?._id === "all-color") {
      setFilteredImages(sortedImages);
      return;
    }

    const colorImages = sortedImages.filter(
      (img) => img?.color?._id == selectedColor?._id
    );
    if (colorImages.length > 0) {
      setFilteredImages(colorImages);
    } else {
      setFilteredImages(sortedImages);
    }
  }, [selectedColor, sortedImages]);

  useEffect(() => {
    setActiveIndex(0);
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, [filteredImages]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isImageViewVisible) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          if (filteredImages?.length > 1) {
            const newIndex =
              activeIndex > 0 ? activeIndex - 1 : filteredImages.length - 1;
            setActiveIndex(newIndex);
          }
          break;
        case "ArrowRight":
          event.preventDefault();
          if (filteredImages?.length > 1) {
            const newIndex =
              activeIndex < filteredImages.length - 1 ? activeIndex + 1 : 0;
            setActiveIndex(newIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsImageViewVisible(false);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isImageViewVisible, activeIndex, filteredImages]);

  if (loading) {
    return (
      <div className="product-details-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-details-container">
        {/* <HomeHeader /> */}
        <Breadcrumb items={breadcrumbItems} />
        <div className="page-content">
          <div className="empty-container">
            <div className="empty-icon">📦</div>
            <p className="empty-text">Product not available at the moment</p>
          </div>
        </div>
        <CommonFooter />
      </div>
    );
  }

  return (
    <div className="product-details-container">
      {/* <HomeHeader fetchDetails={getCartProducts} /> */}
      <Breadcrumb items={breadcrumbItems} />
      <div className="product-details-content">
        {/* Left Column - Image Section */}
        <div className="image-section">
          <div className="image-gallery-container">
            {/* Thumbnail Gallery - Left Side */}
            {filteredImages?.length > 1 && (
              <div className="thumbnail-gallery-left">
                {filteredImages?.map((img, idx) => (
                  <div
                    key={idx}
                    className={`thumbnail-wrapper ${activeIndex === idx ? "active" : ""
                      }`}
                    onClick={() => {
                      setActiveIndex(idx);
                    }}
                  >
                    <LazyImage
                      src={getOptimizedUrl(img?.image)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="thumbnail"
                      shimmerClassName="thumbnail-shimmer"
                      loading="lazy"
                    />
                    <div className="thumbnail-overlay"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Main Image Container */}
            <div className="main-image-container">
              <div className="image-wrapper">
                <LazyImage
                  src={getOptimizedUrl(filteredImages[activeIndex]?.image)}
                  alt={`Product ${activeIndex + 1}`}
                  className="product-image"
                  shimmerClassName="main-image-shimmer"
                  onClick={() => setIsImageViewVisible(true)}
                  onMouseMove={handleMouseMove}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  ref={imageRef}
                  loading="lazy"
                />

                {/* Zoom Overlay */}
                {/* {isZoomed && (
                  <div className="zoom-overlay">
                    <div 
                      className="magnifier"
                      style={{
                        left: `${zoomPosition.x}%`,
                        top: `${zoomPosition.y}%`,
                        transform: 'translate(-50%, -50%)',
                        backgroundImage: `url(${getImageUrl(filteredImages[activeIndex]?.image)})`,
                        backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        backgroundSize: '200% 200%',
                      }}
                    />
                  </div>
                )} */}
              </div>

              {/* Separate Zoom View */}
              {/* {isZoomed && preloadZoomView && (
                <div className="separate-zoom-view">
                  <div 
                    className="zoom-view-content"
                    style={{
                      backgroundImage: `url(${preloadZoomView})`,
                      backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                      backgroundSize: '400% 400%',
                    }}
                  />
                </div>
              )} */}

              <div className="main-image-gallery-controls">
                {filteredImages?.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="nav-arrow nav-arrow-left"
                      aria-label="Previous image"
                      onClick={() => {
                        const newIndex =
                          activeIndex > 0
                            ? activeIndex - 1
                            : filteredImages.length - 1;
                        setActiveIndex(newIndex);
                      }}
                    >
                      <FaChevronLeft aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="nav-arrow nav-arrow-right"
                      aria-label="Next image"
                      onClick={() => {
                        const newIndex =
                          activeIndex < filteredImages.length - 1
                            ? activeIndex + 1
                            : 0;
                        setActiveIndex(newIndex);
                      }}
                    >
                      <FaChevronRight aria-hidden />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="zoom-button"
                  aria-label="Expand image"
                  onClick={() => setIsImageViewVisible(true)}
                >
                  <FaExpand aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Product Information */}
        <div className="product-info-section">
          <div className="product-info">
            <h1 className="detail-product-name">{product?.name}</h1>
            {productPrice && (
              <p className="product-price">
                {RUPEE}{productPrice}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              type="button"
              className="action-button wishlist-button"
              onClick={onPressWishlist}
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              {isWishlisted ? (
                <FaHeart className="heart-icon active" color={colors.red} />
              ) : (
                <FaRegHeart className="heart-icon" />
              )}
            </button>
            <button
              type="button"
              className="action-button download-button"
              onClick={handleDownloadImagesSingle}
              aria-label="Download images"
            >
              <FaDownload className="download-icon" />
            </button>
            <button
              type="button"
              className="action-button zip-button"
              onClick={handleDownloadImagesAsZip}
              aria-label="Download images as ZIP"
            >
              <FaFileArchive className="zip-icon" />
            </button>
            <button
              type="button"
              className="action-button inquiry-button"
              onClick={goToProductInquiry}
              aria-label="Inquire about this product"
              title="Product inquiry"
            >
              <img
                src={imagePath.question}
                alt=""
                className="inquiry-icon-img"
              />
            </button>
          </div>

          {/* Color Selection */}
          {displayColors?.length > 0 && (
            <div className="colors-section">
              <h3 className="section-title">
                Color:{" "}
                {selectedColor?._id === "all-color"
                  ? "ALL"
                  : selectedColor?.name === "No Color"
                    ? "NO"
                    : selectedColor?.name?.toUpperCase()}
              </h3>
              <div className="color-container">
                {displayColors?.map((color, key) => (
                  <button
                    key={key}
                    className={`color-box-container ${selectedColor?._id === color?._id ? "active" : ""
                      }`}
                    onClick={() => handleColorSelection(color)}
                  >
                    <span className="color-text">
                      {color?.name === "No Color" ? "No" : color?.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {product?.sizes?.length > 0 && (
            <div className="size-section">
              <h3 className="section-title">Size: {selectedSize?.size}</h3>
              <div className="size-container">
                {product?.sizes?.map((size, key) => (
                  <button
                    key={key}
                    className={`size-box ${selectedSize?._id === size?._id ? "active" : ""
                      } ${!isProductInStock(size?.unit) ? "out-of-stock" : ""}`}
                    onClick={() => setSelectedSize(size)}
                    disabled={!isProductInStock(size?.unit)}
                  >
                    <span
                      className={`size-text ${selectedSize?._id === size?._id ? "active" : ""
                        }`}
                    >
                      {size?.size}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product Details */}
          {(product?.fabric || product?.category?.name || product?.brand?.name) && (
            <div className="product-details-section">
              <h3 className="section-title">Product Details</h3>
              {product?.fabric && (
                <div className="detail-container">
                  <span className="detail-title">Fabric:</span>
                  <span className="detail-text">{product?.fabric}</span>
                </div>
              )}
              {product?.category?.name && (
                <div className="detail-container">
                  <span className="detail-title">Category:</span>
                  <span className="detail-text">{product.category.name}</span>
                </div>
              )}
              {product?.brand?.name && (
                <div className="detail-container">
                  <span className="detail-title">Brand:</span>
                  <span className="detail-text">{product?.brand?.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          {hasInStockSize ? (
            <div className="cart-button-container">
              {isProductInCart ? (
                <CommonButton
                  title="Go to Cart"
                  onPress={openCartDrawer}
                  customClassName="cart-button cart-button-primary"
                />
              ) : (
                <CommonButton
                  title={cartLoading ? "Adding..." : "Add to Cart"}
                  onPress={handleAddToCart}
                  customClassName="cart-button cart-button-primary"
                  disabled={cartLoading}
                />
              )}
            </div>
          ) : (
            <div className="cart-button-container">
              <CommonButton
                title={"Out of Stock"}
                customClassName="cart-button cart-button-primary"
                disabled={true}
              />
            </div>
          )}
        </div>

        {/* Product Accordions (Positioned via Grid on Desktop) */}
        <div className="product-accordions">
          {product?.description && (
            <div className="accordion-item">
              <button
                className="accordion-header"
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              >
                <div className="header-left">
                  <FaFileAlt className="accordion-icon-react" />
                  <span>DESCRIPTION</span>
                </div>
                <span className="accordion-toggle">{descriptionExpanded ? '−' : '+'}</span>
              </button>
              <div className={`accordion-collapse ${descriptionExpanded ? 'expanded' : ''}`}>
                <div className="accordion-content" dangerouslySetInnerHTML={{ __html: product?.description }} />
              </div>
            </div>
          )}

          {product?.return_policy && (
            <div className="accordion-item">
              <button
                className="accordion-header"
                onClick={() => setReturnPolicyExpanded(!returnPolicyExpanded)}
              >
                <div className="header-left">
                  <FaSyncAlt className="accordion-icon-react" />
                  <span>RETURN & EXCHANGE</span>
                </div>
                <span className="accordion-toggle">{returnPolicyExpanded ? '−' : '+'}</span>
              </button>
              <div className={`accordion-collapse ${returnPolicyExpanded ? 'expanded' : ''}`}>
                <div className="accordion-content" dangerouslySetInnerHTML={{ __html: product?.return_policy }} />
              </div>
            </div>
          )}
        </div>

        {/* Sticky Trigger Point marker */}
        <div ref={reviewsSectionRef} style={{ height: '1px' }}></div>

        {/* Mobile-only Reviews Section (Hidden on Desktop via CSS) */}
        <div className="reviews-mobile-section">
          <h3 className="section-title">Rating & Reviews ({product?.rating_reviews?.length || 0})</h3>
          <div className="reviews-list-mobile">
            {product?.rating_reviews?.length > 0 ? (
              product?.rating_reviews?.map((review, key) => (
                <div key={key} className="review-item-mobile">
                  <div className="review-header-mobile">
                    <div className="avatar-circle-small">
                      {getInitials(review?.user?.name)}
                    </div>
                    <div className="user-info-mobile">
                      <h4>{review?.user?.name}</h4>
                      {review?.rating && (
                        <div className="stars-mobile">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} className={star <= review?.rating ? "filled" : ""}>★</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {review?.review && <p className="review-text-mobile">{review?.review}</p>}
                </div>
              ))
            ) : (
              <p className="no-reviews-text">No reviews available yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Reviews Section - Full Width */}
      <div className="reviews-full-section">
        <div className="reviews-full-content">
          <h3 className="reviews-full-title">Rating & Reviews ({product?.rating_reviews?.length || 0})</h3>
          <div className="reviews-list">
            {product?.rating_reviews?.length > 0 ? (
              product?.rating_reviews?.map((review, key) => (
                <div key={key} className="review-item">
                  <div className="avatar-circle">
                    <span className="avatar-text">
                      {getInitials(review?.user?.name)}
                    </span>
                  </div>
                  <div className="user-details">
                    <h4 className="review-user-name">{review?.user?.name}</h4>
                    {review?.rating && review?.rating !== 0 && (
                      <div className="detail-rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`star ${star <= review?.rating ? "filled" : ""
                              }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    )}
                    {review?.review && (
                      <p className="review-text">{review?.review}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-reviews-text">No reviews available yet.</p>
            )}
          </div>
        </div>
      </div>


      {/* Related products — after description / return policy tabs */}
      {relatedProducts.length > 0 && (
        <section
          className="related-products-section"
          aria-labelledby="related-products-heading"
        >
          <div className="related-products__inner">
            <h2
              id="related-products-heading"
              className="related-products__title"
            >
              Related products
            </h2>
            <div className="related-products__scroller">
              {relatedProducts.map((item) => (
                <div
                  key={item._id || item?.id}
                  className="related-products__card"
                >
                  <ProductCard item={item} singleImage />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related products   */}
      {recentlyViewedProducts.length > 0 && (
        <section
          className="related-products-section"
          aria-labelledby="recently-viewed-heading"
        >
          <div className="related-products__inner">
            <h2
              id="recently-viewed-heading"
              className="related-products__title"
            >
              Recently viewed
            </h2>
            <div className="related-products__scroller">
              {recentlyViewedProducts.map((item) => (
                <div
                  key={item._id || item?.id}
                  className="related-products__card"
                >
                  <ProductCard item={item} singleImage />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ImageViewer
        images={filteredImages?.map((img) => getImageUrl(img?.image)) || []}
        currentIndex={activeIndex}
        isVisible={isImageViewVisible}
        onClose={() => setIsImageViewVisible(false)}
        onNext={() => {
          const newIndex =
            activeIndex < filteredImages.length - 1 ? activeIndex + 1 : 0;
          setActiveIndex(newIndex);
        }}
        onPrevious={() => {
          const newIndex =
            activeIndex > 0 ? activeIndex - 1 : filteredImages.length - 1;
          setActiveIndex(newIndex);
        }}
      />

      {downloading && (
        <div className="downloading-overlay">
          <div className="downloading-content">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
            <p>Downloading... {downloadProgress}%</p>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartDrawerOpen} onClose={closeCartDrawer} />

      {/* Sticky Add to Cart Bar */}
      {showStickyBar && product && (
        <div className={`sticky-add-to-cart ${showStickyBar ? "visible" : ""}`}>
          <div className="sticky-content-left">
            <div className="sticky-img-container">
              <img
                src={getImageUrl(sortedImages[0]?.image)}
                alt={product?.name}
                className="sticky-product-image"
              />
            </div>
            <div className="sticky-product-info">
              <h4 className="sticky-product-name">{product?.name}</h4>
              <p className="sticky-product-price">₹ {productPrice}</p>
            </div>
          </div>
          <div className="sticky-content-right">
            {hasInStockSize ? (
              isProductInCart ? (
                <CommonButton
                  title="Go to Cart"
                  onPress={openCartDrawer}
                  customClassName="sticky-cart-btn"
                />
              ) : (
                <CommonButton
                  title={cartLoading ? "Adding..." : "Add to Cart"}
                  onPress={handleAddToCart}
                  customClassName="sticky-cart-btn"
                  disabled={cartLoading}
                />
              )
            ) : (
              <span className="out-of-stock-badge">Out of Stock</span>
            )}
          </div>
        </div>
      )}

      <div ref={footerRef}>
        <CommonFooter
          productInfo={{
            name: product?.name || "Product",
            link: `https://yugmantra.com/product/${product?.slug}`,
            image: getImageUrl(sortedImages[0]?.image)
          }}
        />
      </div>
    </div>
  );
};

export default ProductDetails;
